import { Alert, Divider, Flex, Form, Space } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { REACT_APP_HOSPITAL_CODE } from 'src/enums';
import { ApiService } from 'src/services';
import { userSelectors } from 'src/store';
import { useTheme } from 'src/styles/theme';
import {
  getErrorMessage,
  ISRAELI_PHONE_REGEX,
  isValidIsraeliID,
  notificationError,
  notificationSuccess,
  translateError
} from 'src/utils';

import classes from '../../screens/login/style/Login.module.css';
import AppFormItem from '../appFormItem';
import AppInput from '../appInput';
import AppPrimaryButton from '../appPrimaryButton/AppPrimaryButton';
import SupportContactModal from '../supportContactModal';
import TakanonModal from '../takanonModal';

const ALERT_WRAPPER = {
  width: '100%',
  padding: 5,
  marginBottom: 20
};
interface IProps {
  onSubmit: (values: { userId: string; phoneNumber: string }) => void;
  isLoading: boolean;
  errorMessage?: string | null;
  inputWrapperClassName?: string;
  isModalVisible: boolean;
  setModalOpen: (value: boolean) => void;
}

const LoginForm = (props: IProps) => {
  // TOOLS
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const theme = useTheme();
  // PROPS
  const { onSubmit, isLoading, errorMessage, setModalOpen, isModalVisible } =
    props;

  // STATE

  const [supportModalState, setSupportModalState] = React.useState<{
    loader: boolean;
    error: null | string;
    success: boolean;
  }>({ loader: false, error: null, success: false });

  // SELECTORS
  const hospital = useSelector(userSelectors.selectUserHospital);

  // REFS
  const userNameInputRef = React.useRef<HTMLInputElement>();

  // FOR LOCAL
  const fillData = () => {
    form.setFieldsValue({
      userId: '204399844',
      phoneNumber: '0545371136'
      // userId: '112233440',
      // phoneNumber: '0524210453',
    });
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const onSubmitSupportModal = async (values: {
    hospitalCode: string;
    userName: string;
    phoneNumber: string;
    subject: string;
    content: string;
  }) => {
    if (!REACT_APP_HOSPITAL_CODE) {
      return;
    }
    try {
      setSupportModalState({ loader: true, error: null, success: false });
      const { userName, phoneNumber, subject, content } = values;
      await ApiService.sendEmailToSupport({
        userName: userName,
        subject: subject,
        content: content,
        phoneNumber: phoneNumber,
        hospitalCode: REACT_APP_HOSPITAL_CODE
      });
      setSupportModalState({ loader: false, error: null, success: true });
      notificationSuccess({
        message:
          hospital?.messageAfterContacting || t('login.contactSupportSent')
      });
      closeModal();
    } catch (error) {
      const errorMessage = translateError(getErrorMessage(error));
      notificationError({
        message: errorMessage
      });
      setSupportModalState({
        loader: false,
        error: errorMessage,
        success: false
      });
    }
  };

  return (
    <Flex
      align="center"
      justify="center"
      className={classes.formInnerContainer}
    >
      <Form
        form={form}
        name="basic"
        initialValues={{ remember: true }}
        onFinish={onSubmit}
        className={`${classes.form}`}
      >
        <Space direction="vertical" size="small" className={classes.formTitle}>
          <h1 className={`${theme.fonts.classNames.subPageTitle} `}>
            {t('login.title')}
          </h1>
          <h1 className={`${theme.fonts.classNames.pageTitle} `}>
            {t('login.subtitle')}
          </h1>
        </Space>
        <Flex
          justify="center"
          className={classes.mobileLogo}
          vertical
          align="center"
        >
          <img
            className={`${classes.logo} `}
            src={hospital?.logo || '/assets/space_saver.png'}
            alt={t('accessibility.ministryOfHealthLogo')}
            title={t('accessibility.ministryOfHealthLogo')}
          />
          {/* <p className={`${theme.fonts.classNames.cardTitle}`}>
            {`${t('login.medicalCenter')} ${hospital?.name}`}{' '}
          </p> */}
        </Flex>

        <Flex vertical>
          <AppFormItem
            aria-label={t('login.userId')}
            aria-required="true"
            label={t('login.userId')}
            name="userId"
            validateTrigger={'onBlur'}
            rules={[
              { required: true, message: t('erros.requiredField') },
              {
                message: t('erros.invalid_id'),
                validator: (_, value) => {
                  if (!value || isValidIsraeliID(value)) {
                    return Promise.resolve();
                  } else {
                    return Promise.reject();
                  }
                }
              }
            ]}
          >
            <AppInput inputRef={userNameInputRef} type={'tel'} />
          </AppFormItem>

          <AppFormItem
            aria-label={t('login.phoneNumber')}
            aria-required="true"
            name="phoneNumber"
            label={t('login.phoneNumber')}
            validateTrigger={'onBlur'}
            rules={[
              { required: true, message: t('erros.requiredField') },
              {
                pattern: ISRAELI_PHONE_REGEX,
                message: t('erros.invalid_phone_number')
              }
            ]}
          >
            <AppInput id="phoneNumber" type={'tel'} />
          </AppFormItem>
          {errorMessage && (
            <div style={ALERT_WRAPPER}>
              <Alert message={errorMessage} type="error" />
            </div>
          )}
          <AppFormItem>
            {REACT_APP_HOSPITAL_CODE && <TakanonModal />}
          </AppFormItem>
          <AppPrimaryButton
            htmlType="submit"
            disabled={isLoading}
            loading={isLoading}
          >
            {t('login.submit')}
          </AppPrimaryButton>
          {/* {process.env.REACT_APP_ENV === 'dev' && (
            <AppPrimaryButton
              style={{ marginTop: 10 }}
              onClick={fillData}
              className={`${classes.submit} ${theme.fonts.classNames.button}`}
              disabled={isLoading}
            >
              {'fiil data'}
            </AppPrimaryButton>
          )} */}
        </Flex>

        <Divider />
        {hospital?.showContactPopUp && (
          <div>
            {isModalVisible && ( // for reset fields
              <SupportContactModal
                isSubmitted={supportModalState.success}
                isModalVisible={isModalVisible}
                closeModal={closeModal}
                onSubmit={onSubmitSupportModal}
                hospital={hospital}
                supportModalState={supportModalState}
              />
            )}
            <Flex justify="center">
              <div className={`${theme.fonts.classNames.comment}`}>
                {t('login.cantLoginBefore')}
                <span
                  className={classes.contactSupportLink}
                  onClick={() => setModalOpen(true)}
                >
                  {t('login.clickHere')}
                </span>
                {t('login.cantLoginAfter')}
              </div>
            </Flex>
          </div>
        )}
      </Form>
    </Flex>
  );
};

export default LoginForm;
